
import { defineAsyncComponent, PropType } from "vue";
import { Options, prop, Vue } from "vue-class-component";
import { ClientNamePOSData } from "@/domain/entities/Partner";
import { OptionsClass } from "@/domain/entities/MainApp";

const DaftarKlienPopupV4 = defineAsyncComponent({
  loader: () => import("./daftar-klien-popup.vue")
});

class Props {
  showPicker = prop<boolean>({ default: true });
  selectedClient = prop<ClientNamePOSData | null>({
    default: null,
    type: Object as PropType<ClientNamePOSData | null>
  });
  errorMessage = prop<string>({ default: "" });
  isDisabled = prop<boolean>({ default: false });
  clients = prop<ClientNamePOSData[]>({ default: () => [] });
  origins = prop<OptionsClass[]>({ default: () => [] });
  selectedOrigin = prop<OptionsClass | null>({
    default: null,
    type: Object as PropType<OptionsClass | null>
  });
  selectedServiceTypes = prop<OptionsClass[]>({ default: () => [] });
  searchKeyword = prop<string>({ default: "" });
  isInitialLoading = prop<boolean>({ default: false });
  isLoadingMore = prop<boolean>({ default: false });
  hasNextPage = prop<boolean>({ default: false });
  listErrorMessage = prop<string>({ default: "" });
  isOriginLoading = prop<boolean>({ default: false });
  originsHasNextPage = prop<boolean>({ default: false });
}

@Options({
  name: "FieldKlienV4",
  components: { DaftarKlienPopupV4 },
  emits: [
    "blur",
    "clear",
    "close-list",
    "focus",
    "load-more",
    "open-list",
    "origin-filter",
    "origin-focus",
    "origin-scrolled",
    "retry",
    "select",
    "tab",
    "update:origin",
    "update:search",
    "update:selected-client",
    "update:service-types"
  ]
})
export default class FieldKlienV4 extends Vue.with(Props) {
  showMainResults = false;
  popupVisible = false;
  suppressInitialFocusOpen = false;
  mainResultButtons: HTMLButtonElement[] = [];

  mounted(): void {
    if (!this.selectedClient && !this.isDisabled) {
      this.suppressInitialFocusOpen = true;
      this.$nextTick(() => {
        (this.$refs.trigger as HTMLInputElement)?.focus();
        this.suppressInitialFocusOpen = false;
      });
    }
  }

  get fieldValue(): string {
    return this.selectedClient
      ? this.clientLabel(this.selectedClient)
      : this.searchKeyword;
  }

  get serviceTypeOptions(): OptionsClass[] {
    return [
      new OptionsClass({ name: "COD", label: "COD", value: "cod" }),
      new OptionsClass({ name: "DFOD", label: "DFOD", value: "dfod" }),
      new OptionsClass({ name: "DO", label: "DO", value: "do" }),
      new OptionsClass({ name: "Non-COD", label: "Non-COD", value: "non_cod" })
    ];
  }

  clientLabel(client: ClientNamePOSData): string {
    return client.clientAlias
      ? `${client.clientAlias} - ${client.clientName}`
      : client.clientName;
  }

  onMainSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (this.selectedClient) {
      this.$emit("update:selected-client", null);
      this.$emit("clear");
    }
    this.showMainResults = true;
    this.$emit("update:search", value);
  }

  onMainFocus(): void {
    if (this.isDisabled) return;
    if (this.suppressInitialFocusOpen) {
      this.$emit("focus");
      return;
    }
    this.showMainResults = true;
    this.$emit("focus");
    
  }
  onMainClick(): void {
    if (this.isDisabled) return;
    this.showMainResults = true;
    this.$emit("focus");
  }

  onMainBlur(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as Node | null;
    const root = this.$refs.root as HTMLElement | null;
    if (root && relatedTarget && root.contains(relatedTarget)) {
      return;
    }
    window.setTimeout(() => {
      this.showMainResults = false;
      if (!this.popupVisible) this.$emit("blur");
    }, 0);
  }

  onMainTab(): void {
    this.showMainResults = false;
    this.$emit('tab');
  }

  closeMainResults(): void {
    this.showMainResults = false;
  }

  onTriggerArrowDown(): void {
    if (this.isDisabled) return;
    this.showMainResults = true;
    this.$nextTick(() => {
      this.mainResultButtons[0]?.focus();
    });
  }

  onTriggerArrowUp(): void {
    if (!this.showMainResults) return;
    this.mainResultButtons[this.mainResultButtons.length - 1]?.focus();
  }

  onResultArrowDown(index: number): void {
    const nextIndex = index + 1;
    if (nextIndex < this.mainResultButtons.length) {
      this.mainResultButtons[nextIndex]?.focus();
    }
  }

  onResultArrowUp(index: number): void {
    if (index > 0) {
      this.mainResultButtons[index - 1]?.focus();
    } else {
      this.mainResultButtons[0]?.blur();
      (this.$refs.trigger as HTMLInputElement)?.focus();
    }
  }

  openList(): void {
    if (this.isDisabled) return;
    this.showMainResults = false;
    this.popupVisible = true;
    this.$emit("open-list");
  }

  closeList(): void {
    this.popupVisible = false;
    this.$emit("close-list");
    this.$nextTick(() => {
      (this.$refs.trigger as HTMLInputElement)?.focus();
    });
  }

  updateSearch(value: string): void {
    this.$emit("update:search", value);
  }

  updateOrigin(value: OptionsClass | null): void {
    this.$emit("update:origin", value);
  }

  updateServiceTypes(value: OptionsClass[]): void {
    this.$emit("update:service-types", value);
  }

  resetFilters(): void {
    this.$emit("update:origin", null);
    this.$emit("update:service-types", []);
  }

  selectClient(client: ClientNamePOSData): void {
    this.showMainResults = false;
    this.$emit("update:selected-client", client);
    this.$emit("select", client);
    if (this.popupVisible) this.closeList();
    (this.$refs.trigger as HTMLInputElement)?.blur();
  }

  clearSelection(): void {
    this.$emit("update:selected-client", null);
    this.$emit("clear");
  }
}
